import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTranslations } from 'next-intl';
import { markJustLoggedIn } from '@/lib/loginFlag';

// Auth Components
import { AuthLayout } from './auth/AuthLayout';
import { LoginForm } from './auth/LoginForm';
import { SignUpForm } from './auth/SignUpForm';
import { GoogleAuthButton } from './auth/GoogleAuthButton';
import { VerificationSentView } from './auth/VerificationSentView';
import { ConflictResolutionView } from './auth/ConflictResolutionView';
import { ForgotPasswordForm } from './auth/ForgotPasswordForm';

interface PortalEntryProps {
  onEnter: () => void;
}

type AuthView = 'login' | 'signup' | 'verification-sent' | 'conflict' | 'forgot-password';

const SIGNUP_API_URL = '/api/auth/signup';

// The divider between the credential form and the Google control. Rendered on
// both the login and signup views, which is why it is spelled once.
const AuthDivider: React.FC<{ label: string }> = ({ label }) => (
  <div className="my-7 flex items-center gap-4">
    <span className="flex-1 border-t border-border" aria-hidden="true" />
    <span className="text-[11px] font-semibold uppercase tracking-[0.09em] text-fg-muted">{label}</span>
    <span className="flex-1 border-t border-border" aria-hidden="true" />
  </div>
);

const PortalEntry: React.FC<PortalEntryProps> = ({ onEnter }) => {
  const t = useTranslations('auth');

  // No caught error's own message is ever rendered. A verbatim provider string
  // distinguishes an unknown account from a wrong password, which is an
  // account-enumeration oracle (T-08-07-01). A rejected `fetch` surfaces as a
  // TypeError and is a connectivity failure; everything else that reaches these
  // handlers is a credential or provider rejection.
  const authErrorMessage = (err: unknown): string =>
    err instanceof TypeError ? t('errors.network') : t('errors.signInFailed');

  // Auth State
  const [currentView, setCurrentView] = useState<AuthView>('login');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Form Data
  const [verificationEmail, setVerificationEmail] = useState('');
  const [conflictEmail, setConflictEmail] = useState('');
  const [conflictProvider, setConflictProvider] = useState<'google' | 'email'>('google');

  useEffect(() => {
    // Surface an OAuth callback failure forwarded as ?error / ?error_description
    // so a failed sign-in shows feedback instead of silently returning to login.
    // Clear the params afterward so a refresh doesn't re-show a stale error.
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const desc = params.get('error_description');
      const code = params.get('error');
      if (desc || code) {
        // `error_description` is attacker-controlled URL input. It stays out of
        // the DOM entirely and the app substitutes its own copy (T-08-07-02).
        console.error('OAuth callback error:', code, desc);
        setAuthError(t('errors.signInFailed'));
        params.delete('error');
        params.delete('error_description');
        const qs = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
      }
    }
  }, [t]);

  useEffect(() => {
    // Probe session in the background. We render the form immediately —
    // already-authed users get bounced to /dashboard when the probe resolves.
    // Keeps the LCP element (wordmark) off the network-round-trip critical path.
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) onEnter();
    });
  }, []);

  // Email/Password Login
  const handleEmailLogin = async (email: string, password: string) => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) throw signInError;

      markJustLoggedIn();
      onEnter();
    } catch (err: unknown) {
      console.error('Email sign-in failed:', err);
      setAuthError(authErrorMessage(err));
    } finally {
      setAuthLoading(false);
    }
  };

  // Email Sign Up (via Internal API)
  const handleEmailSignUp = async (email: string, password: string, name: string, consentC: boolean) => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      const response = await fetch(SIGNUP_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, name, consent_c: consentC })
      });

      const data = await response.json().catch(() => ({}));

      if (!data || Object.keys(data).length === 0) {
        throw new Error('Invalid response from server.');
      }

      // Check for error in response body (n8n might return 200 with error in body)
      const errorMessage = data.error || data.message || data.msg || '';
      const isAlreadyRegistered =
        errorMessage.toLowerCase().includes('already been registered') ||
        errorMessage.toLowerCase().includes('already exists') ||
        errorMessage.toLowerCase().includes('user_already_exists') ||
        data.code === 'user_already_exists';

      if (!response.ok || isAlreadyRegistered) {
        // Handle 409 Conflict or "already registered" error
        if (response.status === 409 || isAlreadyRegistered) {
          const existingProvider = data.existingProvider || 'email';
          handleConflict(email, existingProvider);
          return;
        }

        throw new Error(errorMessage || `Sign up failed: ${response.statusText}`);
      }

      // Success - show verification sent view
      setVerificationEmail(email);
      setCurrentView('verification-sent');

    } catch (err: unknown) {
      console.error('Sign-up failed:', err);
      setAuthError(authErrorMessage(err));
    } finally {
      setAuthLoading(false);
    }
  };

  // Google sign-in goes straight to Supabase's OAuth redirect flow.
  //
  // We previously tried GIS One Tap (prompt()) on click and only fell back to
  // the redirect after the moment notification reported a dismissal. Chrome's
  // FedCM migration deprecated those notification methods (isNotDisplayed /
  // isSkippedMoment), so a suppressed One Tap reported nothing, the fallback
  // never fired, and the button silently did nothing. The redirect flow is the
  // reliable path under FedCM.
  const handleGoogleLogin = async () => {
    setAuthError(null);
    await startGoogleOAuthRedirect();
  };

  // Supabase redirect-based OAuth — used as a fallback when FedCM is in cooldown
  // or the GIS script has not loaded. Supabase handles the round-trip and drops
  // the user back at `/`, where the proxy routes them onward.
  const startGoogleOAuthRedirect = async () => {
    setAuthLoading(true);
    try {
      // Flag now so the post-OAuth bounce back to / → /dashboard sees it.
      // sessionStorage survives the same-origin redirect round-trip.
      markJustLoggedIn();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo:
            typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined,
        },
      });
      if (error) throw error;
    } catch (err: unknown) {
      console.error('Google sign-in failed:', err);
      setAuthError(authErrorMessage(err));
      setAuthLoading(false);
    }
  };

  // Conflict Handler
  const handleConflict = (email: string, existingProvider: 'google' | 'email') => {
    setConflictEmail(email);
    setConflictProvider(existingProvider);
    setCurrentView('conflict');
    setAuthLoading(false);
  };

  // View Switchers
  const switchToLogin = () => {
    setCurrentView('login');
    setAuthError(null);
  };

  const switchToSignUp = () => {
    setCurrentView('signup');
    setAuthError(null);
  };

  const switchToForgotPassword = () => {
    setCurrentView('forgot-password');
    setAuthError(null);
  };

  return (
      <AuthLayout>
        {currentView === 'login' && (
          <>
            <LoginForm
              onSubmit={handleEmailLogin}
              loading={authLoading}
              error={authError}
              onSwitchToSignUp={switchToSignUp}
              onForgotPassword={switchToForgotPassword}
            />

            <AuthDivider label={t('networkProtocol')} />

            <GoogleAuthButton onClick={handleGoogleLogin} />
          </>
        )}

        {currentView === 'signup' && (
          <>
            <SignUpForm
              onSubmit={handleEmailSignUp}
              loading={authLoading}
              error={authError}
              onConflict={handleConflict}
              onSwitchToLogin={switchToLogin}
            />

            <AuthDivider label={t('networkProtocol')} />

            <GoogleAuthButton onClick={handleGoogleLogin} />
          </>
        )}

        {currentView === 'verification-sent' && (
          <VerificationSentView
            email={verificationEmail}
            onReturnToLogin={switchToLogin}
          />
        )}

        {currentView === 'forgot-password' && (
          <ForgotPasswordForm onBack={switchToLogin} />
        )}

        {currentView === 'conflict' && (
          <ConflictResolutionView
            email={conflictEmail}
            existingProvider={conflictProvider}
            attemptedProvider="email"
            onSignInWithGoogle={handleGoogleLogin}
            onGoBack={switchToSignUp}
            onGoToLogin={switchToLogin}
          />
        )}
      </AuthLayout>
  );
};

export default PortalEntry;